import { RefObject, useCallback, useEffect, useRef } from 'react';

const GESTURE_WINDOW_MS = 1000;

/*
  Tells load-more whether a live user gesture happened recently, and feeds upward
  wheels to the paginate check directly.

  Both exist for the same reason: a merged page changes scrollHeight, and near the
  top the browser's scroll anchoring emits its own scroll events while the viewport
  still sits inside the load-more threshold — without the gate that chains fetch
  after fetch with no user input until the whole thread is loaded. And pinned at the
  very top, an upward wheel cannot move scrollTop, so no scroll event fires at all —
  the wheel listener re-runs the threshold check itself.
*/
export function useLoadMoreGestureGate(
  chatRef: RefObject<HTMLDivElement | null>,
  onUpwardWheel: (el: HTMLElement) => void,
): { isGestureFresh: () => boolean } {
  const lastGestureAtRef = useRef(0);
  const onUpwardWheelRef = useRef(onUpwardWheel);
  onUpwardWheelRef.current = onUpwardWheel;

  useEffect(() => {
    const el = chatRef.current;
    if (!el) return;

    const mark = () => {
      lastGestureAtRef.current = performance.now();
    };
    const onWheel = (ev: WheelEvent) => {
      mark();
      if (ev.deltaY < 0) {
        onUpwardWheelRef.current(el);
      }
    };
    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('touchmove', mark, { passive: true });
    // scrollbar-thumb drags produce no wheel events, only a mousedown on the scroller
    el.addEventListener('mousedown', mark);
    el.addEventListener('keydown', mark);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchmove', mark);
      el.removeEventListener('mousedown', mark);
      el.removeEventListener('keydown', mark);
    };
  }, [chatRef]);

  const isGestureFresh = useCallback(
    () => performance.now() - lastGestureAtRef.current <= GESTURE_WINDOW_MS,
    [],
  );

  return { isGestureFresh };
}
